import React, { useState } from 'react';
import { Award, Users, Clock, Target, ChevronDown, Check } from 'lucide-react';

const About = () => {
  const [expandedValue, setExpandedValue] = useState<number | null>(0);

  const stats = [
    { icon: <Award className="h-6 w-6 text-brand-primary" />, number: '500+', label: 'Projects Delivered' },
    { icon: <Users className="h-6 w-6 text-brand-primary" />, number: '150+', label: 'Satisfied Clients' },
    { icon: <Clock className="h-6 w-6 text-brand-primary" />, number: '10+', label: 'Years of Excellence' },
    { icon: <Target className="h-6 w-6 text-brand-primary" />, number: '99%', label: 'Success Rate' }
  ];

  const values = [
    {
      title: 'Innovation & Research',
      description: 'We stay ahead of technology trends to deliver cutting-edge solutions that give our clients a competitive advantage. This includes integrating AI solutions and building microservices.'
    },
    {
      title: 'Commitment to Quality',
      description: 'Our commitment to excellence ensures every project meets the highest standards of quality and performance, with thorough sanitization, database auditing, and security protocols.'
    },
    {
      title: 'Long-Term Partnership',
      description: 'We build long-term relationships with our clients, acting as trusted technology partners. We align closely with client roadmaps to ensure continuous maintenance and expansion.'
    },
    {
      title: 'Agile Delivery Framework',
      description: 'Our agile approach allows us to adapt quickly to changing requirements, deliver features in bi-weekly increments, and provide full transparency through JIRA boards.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* Intro Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <span className="bg-brand-primary/10 text-brand-dark px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">About Company</span>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-secondary">
              Transforming Visions Into <span className="text-brand-primary">Digital Realities</span>
            </h2>
            <p className="text-gray-600 leading-relaxed text-sm">
              Founded with a vision to bridge the gap between technology and business success, 
              X2InfoTech has been at the forefront of digital innovation for over a decade. 
              We specialize in delivering comprehensive IT solutions that transform businesses 
              and drive sustainable growth.
            </p>
            <p className="text-gray-600 leading-relaxed text-sm">
              Our team of expert developers, consultants, and strategists work collaboratively 
              to understand your unique challenges and craft solutions that not only meet your 
              immediate needs but also position you for future success.
            </p>

            {/* Glowing Stats Cards Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className="bg-white p-4 rounded-xl border border-gray-250/70 hover:border-brand-primary transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex flex-col justify-between"
                >
                  <div className="bg-brand-light p-2 w-fit rounded-lg">{stat.icon}</div>
                  <div className="mt-3">
                    <div className="text-xl font-extrabold text-brand-secondary">{stat.number}</div>
                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Values Accordion */}
          <div className="space-y-6">
            <div className="space-y-2">
              <span className="bg-brand-primary/10 text-brand-dark px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Core Operations</span>
              <h3 className="text-2xl font-bold text-brand-secondary">Our Core Values</h3>
              <p className="text-gray-600 text-xs">Click on any value to explore our engineering guidelines and project principles.</p>
            </div>

            <div className="space-y-3">
              {values.map((value, index) => (
                <div 
                  key={index} 
                  className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden ${
                    expandedValue === index ? 'border-brand-primary shadow-lg shadow-brand-primary/5' : 'border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => setExpandedValue(expandedValue === index ? null : index)}
                    className="w-full p-5 flex justify-between items-center text-left focus:outline-none"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center border text-[10px] font-bold ${
                        expandedValue === index ? 'bg-brand-primary border-brand-primary text-white animate-pulse' : 'border-gray-400 text-gray-500'
                      }`}>
                        <Check className="h-3 w-3" />
                      </div>
                      <span className={`font-bold text-sm ${expandedValue === index ? 'text-brand-primary' : 'text-brand-secondary'}`}>
                        {value.title}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                      expandedValue === index ? 'transform rotate-180' : ''
                    }`} />
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${
                    expandedValue === index ? 'max-h-40 border-t border-gray-100 p-5 bg-gray-50/50' : 'max-h-0'
                  }`}>
                    <p className="text-gray-650 text-xs leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;